import { getCountries, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js/min";

/**
 * Phone numbers for text messages, shared by client and server.
 *
 * Numbers arrive as people type them (Entra, an Admin, a CSV): "(612) 555-0142",
 * "+44 7700 900123", "07700 900123". Texts need one form, E.164 ("+16125550142"),
 * so the same person is found whichever way their number was written. A number
 * with no "+country" is never given a country: it isn't a usable number until
 * its code is added.
 */

/** Every country a phone number can belong to (ISO 3166-1 alpha-2). */
export const PHONE_COUNTRIES: readonly CountryCode[] = getCountries();

export type PhoneCountry = CountryCode;

export function isPhoneCountry(v: unknown): v is PhoneCountry {
  return typeof v === "string" && (PHONE_COUNTRIES as readonly string[]).includes(v);
}

/** A country's name in English, falling back to its code. */
export function phoneCountryName(code: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** The longest a stored number may be, as written (matches the WLM columns). */
export const PHONE_RAW_MAX = 50;

export interface NormalisedPhone {
  /** E.164, e.g. "+16125550142"; null when it isn't a valid number. */
  e164: string | null;
  /** As written, trimmed (at most PHONE_RAW_MAX), so a bad one can be shown and fixed. */
  raw: string;
}

/**
 * A number as written, in E.164. Valid only when the digits make a real number
 * for its country (length and prefix), not merely when it parses.
 */
export function normalisePhone(
  raw: string | null | undefined,
  defaultCountry: string,
): NormalisedPhone {
  const text = (raw ?? "").trim().slice(0, PHONE_RAW_MAX);
  if (!text) return { e164: null, raw: "" };
  const parsed = parsePhoneNumberFromString(
    text,
    isPhoneCountry(defaultCountry) ? defaultCountry : undefined,
  );
  return { e164: parsed && parsed.isValid() ? parsed.number : null, raw: text };
}

/** A number for display, international form ("+1 612 555 0142"); the input when it can't be read. */
export function formatPhone(e164: string): string {
  return parsePhoneNumberFromString(e164)?.formatInternational() ?? e164;
}

/** The last 4 digits only, for people who may not see the whole number. */
export function maskPhone(e164: string): string {
  const digits = e164.replace(/\D/g, "");
  return `••• ••• ${digits.slice(-4)}`;
}
